import { useState, useMemo } from "react";
import { MRT_ColumnFiltersState } from "material-react-table";
import useDebounce from "./useDebounce";
import type { EntityTableFilterProps } from "../components/EntityTable";
import type { Pagination } from "./query/entityQueryClient";

export type FilterFieldKind = "string" | "number" | "stringArray";

export interface FilterFieldSpec {
  /** DTO field name on the server, if it differs from the MRT column id. */
  field?: string;
  kind: FilterFieldKind;
}

export type FilterSpec = Record<string, FilterFieldSpec>;

const isEmptyFilterValue = (value: unknown): boolean => {
  if (value === undefined || value === null || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

const warnedUnknownFilterIds = new Set<string>();
const warnUnknownFilterIdOnce = (id: string): void => {
  if (warnedUnknownFilterIds.has(id)) return;
  warnedUnknownFilterIds.add(id);
  // eslint-disable-next-line no-console
  console.warn(
    `[useServerFilters] column filter id "${id}" is not in the filter spec. ` +
      `The value will not be sent to the server. Add it to the entity's ` +
      `filterSpec (see *Columns.tsx) or set enableColumnFilter: false on the column.`
  );
};

const coerceFilterValue = (
  value: unknown,
  kind: FilterFieldKind
): unknown => {
  if (isEmptyFilterValue(value)) return undefined;
  switch (kind) {
    case "string":
      return typeof value === "string" ? value : String(value);
    case "number": {
      const n = typeof value === "number" ? value : Number(value);
      return Number.isFinite(n) ? n : undefined;
    }
    case "stringArray":
      if (Array.isArray(value)) {
        const strings = value
          .filter((v) => !isEmptyFilterValue(v))
          .map((v) => (typeof v === "string" ? v : String(v)));
        return strings.length > 0 ? strings : undefined;
      }
      return [typeof value === "string" ? value : String(value)];
  }
};

/**
 * Owns column-filter state, base-filter merging, and pagination together so
 * that a filter change and its page-reset land in the same render → exactly
 * one fetch. Consumers pass `pagination` + `setPagination` into
 * `useFetchAll({ externalPagination, externalSetPagination })`.
 */
const useServerFilters = <F extends object>(
  baseFilters: Partial<F>,
  filterSpec: FilterSpec = {}
) => {
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const debouncedColumnFilters = useDebounce(columnFilters);

  // Stable reference for baseFilters keyed on its serialized content. Callers
  // typically pass a fresh object literal each render, which would otherwise
  // defeat memoization downstream.
  const baseFiltersKey = JSON.stringify(baseFilters);
  const stableBaseFilters = useMemo(
    () => baseFilters,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseFiltersKey]
  );

  const serverFilters = useMemo(() => {
    const mapped: Record<string, unknown> = {};
    for (const { id, value } of debouncedColumnFilters) {
      const spec = filterSpec[id];
      if (!spec) {
        if (
          process.env.NODE_ENV === "development" &&
          !isEmptyFilterValue(value)
        ) {
          warnUnknownFilterIdOnce(id);
        }
        continue; // column is not server-filterable; drop silently
      }
      const coerced = coerceFilterValue(value, spec.kind);
      if (coerced === undefined) continue;
      mapped[spec.field ?? id] = coerced;
    }
    // baseFilters spread last — they always win.
    return { ...mapped, ...stableBaseFilters } as Partial<F>;
  }, [debouncedColumnFilters, filterSpec, stableBaseFilters]);

  // Zero pageIndex in the same render that serverFilters changes → one
  // fetch instead of two (React's adjust-state-during-render pattern).
  // Hash the mapped payload, not raw input, so dropped/unknown column ids
  // can't churn the signature.
  const filterSignature = useMemo(
    () => JSON.stringify(serverFilters),
    [serverFilters]
  );
  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 100,
  });
  const [trackedFilterSignature, setTrackedFilterSignature] =
    useState(filterSignature);
  let currentPagination = pagination;
  if (trackedFilterSignature !== filterSignature) {
    setTrackedFilterSignature(filterSignature);
    if (pagination.pageIndex !== 0) {
      currentPagination = { ...pagination, pageIndex: 0 };
      setPagination(currentPagination);
    }
  }

  const entityTableFilterProps: EntityTableFilterProps = useMemo(
    () => ({
      columnFilters,
      onColumnFiltersChange: setColumnFilters,
    }),
    [columnFilters]
  );

  return {
    serverFilters,
    entityTableFilterProps,
    pagination: currentPagination,
    setPagination,
  };
};

export default useServerFilters;
